import { useEffect, useRef, useState } from 'react';
import useAuthenticatedApi, {
    HTTP_STATUS_CODES,
} from '../../../Authentication/components/hooks/useAuthenticatedApi';

// Mirrors `ProcessingFlowEnvironment` in cloud/modules/processing/dataProvider.ts.
const CLOUD_ENVIRONMENT_ID = 2;

interface RawProcessingFlow {
    id: number;
    galleryId: number;
    enabled: boolean;
    environmentId: number;
    flowDefinition?: string;
}

interface CloudProcessingState {
    isLoading: boolean;
    isAvailable: boolean;
    flow: RawProcessingFlow | null;
    error?: string;
}

/**
 * Returns whether the given gallery has a configured & enabled cloud processing
 * flow, intended for gating UI affordances that depend on cloud processing
 * (e.g. the "run cloud processing" toggle in bulk upload).
 *
 * Pass `galleryId === null/undefined` to short-circuit the fetch.
 */
export default function useGalleryCloudProcessing(
    galleryId: number | null | undefined,
): CloudProcessingState {
    const { get } = useAuthenticatedApi();
    const [state, setState] = useState<CloudProcessingState>({
        isLoading: !!galleryId,
        isAvailable: false,
        flow: null,
    });

    // Track the most recent galleryId to ignore stale responses.
    const latestGalleryIdRef = useRef<number | null | undefined>(galleryId);

    useEffect(() => {
        latestGalleryIdRef.current = galleryId;

        if (!galleryId) {
            setState({ isLoading: false, isAvailable: false, flow: null });
            return;
        }

        let cancelled = false;
        setState((prev) => ({ ...prev, isLoading: true }));

        (async () => {
            try {
                const response = await get(
                    `/processing/gallery/${galleryId}`,
                );

                if (cancelled || latestGalleryIdRef.current !== galleryId) {
                    return;
                }

                if (response.status !== HTTP_STATUS_CODES.OK) {
                    setState({
                        isLoading: false,
                        isAvailable: false,
                        flow: null,
                        error: `Failed to load processing flows (${response.status})`,
                    });
                    return;
                }

                const flows: RawProcessingFlow[] = Array.isArray(response.data)
                    ? response.data
                    : [];

                const cloudFlow =
                    flows.find(
                        (flow) =>
                            flow.environmentId === CLOUD_ENVIRONMENT_ID &&
                            flow.enabled,
                    ) || null;

                setState({
                    isLoading: false,
                    isAvailable: !!cloudFlow,
                    flow: cloudFlow,
                });
            } catch (e: any) {
                if (cancelled) return;
                setState({
                    isLoading: false,
                    isAvailable: false,
                    flow: null,
                    error: e?.message || 'Failed to check processing flows',
                });
            }
        })();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [galleryId]);

    return state;
}
